import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import PageLayout from "@/components/PageLayout";
import { usePlan } from "@/hooks/usePlan";
import { useAuth } from "@/contexts/AuthContext";
import { callPredict, QuotaExceededError } from "@/lib/predictClient";
import { parseMarketInput } from "@/lib/marketParser";
import { type PredictionResult } from "@/lib/predictionTypes";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { DECIDE_RESET_EVENT } from "@/components/BottomNav";
import { usePageMeta } from "@/hooks/usePageMeta";
import JsonLd from "@/components/JsonLd";
import { deriveResultStateFull } from "@/lib/resultState";

const HOME_FAQ = [
  { q: "What is Decyzon?", a: "Decyzon is the decision layer for prediction markets. It scans Polymarket to surface mispricing and help you avoid bad trades — with a one-sentence angle explaining what the crowd is missing." },
  { q: "How does Decyzon work?", a: "Every few minutes Decyzon scans live market data, applies a multi-signal model, and surfaces real opportunities only when there is a verified route, fresh price, deep liquidity, and positive edge after costs. Otherwise it tells you to wait." },
  { q: "Is Decyzon a broker?", a: "No. Decyzon does not place trades, hold positions, or take fees. It is independent analysis, not financial advice. You execute on Polymarket directly." },
  { q: "Is Decyzon free?", a: "Yes. Free gives you 3 decisions per day with the verdict, edge direction, and one trap. Edge ($19/mo) unlocks the action plan, signal stack, and live alerts. Operator ($49/mo) adds scenario maps and audit trail." },
  { q: "How is Decyzon funded?", a: "Decyzon charges a subscription for premium tiers. We do not take any percentage of trades, do not run an order book, and have no affiliate or referral arrangement with any prediction-market venue." },
];

// Above-the-fold STATIC imports — must paint immediately, no chunk fetch.
import HeroLiveDecisions from "@/components/HeroLiveDecisions";
import HomeProofStrip from "@/components/HomeProofStrip";
import NoCleanBuyFallback from "@/components/NoCleanBuyFallback";
import LoadingTradeSkeleton from "@/components/prediction/LoadingTradeSkeleton";

// SignedInHome only renders for authenticated users. Anonymous landing
// visitors (the Lighthouse measurement target) never need its chunk, which
// pulls in SinceLastVisitHero, HomeTopBuys, actionState, etc. Defer it.
const SignedInHome = lazy(() => import("@/components/SignedInHome"));
import PasteLinkBar from "@/components/PasteLinkBar";

// Below-the-fold — defer to shrink the initial JS chunk and improve Speed Index.
const EdgeDropHero = lazy(() => import("@/components/EdgeDropHero"));
const WhatIsDecyzon = lazy(() => import("@/components/WhatIsDecyzon"));
const PricingSection = lazy(() => import("@/components/PricingSection"));
const SiteFooter = lazy(() => import("@/components/SiteFooter"));
const UpgradeModal = lazy(() => import("@/components/UpgradeModal"));
// Result surface — only renders after user submits a prediction.
const PredictionResultPage = lazy(() => import("@/components/prediction/PredictionResultPage"));

export default function Index() {
  usePageMeta({
    title: "Decyzon — Independent Prediction Market Decision Tool",
    description: "Decyzon is an independent analysis tool for prediction markets. See what to buy, watch, or avoid — with clear edge, traps, and verified decisions. Not affiliated with any market operator.",
    canonical: "https://www.decyzon.com/",
  });
  // FAQPage schema for AI engines + Google rich results.
  // Rendered via JsonLd component below.
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { plan, refresh: refreshPlan } = usePlan();

  const [input, setInput] = useState(params.get("q") ?? "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PredictionResult | null>(null);
  const [decisionId, setDecisionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<string | undefined>();

  // Decide-reset bridge: BottomNav "Decide" tap (or any caller) clears state.
  // Also handles ?reset=1 on URL when navigating from another route.
  useEffect(() => {
    const reset = () => {
      setInput("");
      setResult(null);
      setDecisionId(null);
      setError(null);
      setLoading(false);
      // Focus textarea for immediate next decision
      setTimeout(() => {
        const ta = document.querySelector<HTMLTextAreaElement>("textarea");
        ta?.focus();
      }, 50);
    };
    window.addEventListener(DECIDE_RESET_EVENT, reset);
    return () => window.removeEventListener(DECIDE_RESET_EVENT, reset);
  }, []);

  useEffect(() => {
    if (params.get("reset") === "1") {
      setInput("");
      setResult(null);
      setDecisionId(null);
      setError(null);
      params.delete("reset");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("reset")]);

  const parsed = parseMarketInput(input);

  const run = async (q: string, cid: string | null = null) => {
    if (!q.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setDecisionId(null);
    try {
      // SCANNER TRUTH FIRST: when we came from Feed with a cid, fetch the live
      // scanner row and use it as primary truth. predict() then enriches but
      // must not override the decision/options the scanner produced.
      let scannerRow: any = null;
      if (cid) {
        try {
          const { data } = await supabase
            .from("market_feed")
            .select("*")
            .eq("market_id", cid)
            .order("scanned_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          scannerRow = data ?? null;
        } catch { /* noop */ }
      }
      const res = await callPredict(q, { cid });
      // Overlay: scanner row is primary truth for decision/options when present.
      const merged: PredictionResult = scannerRow ? {
        ...res,
        market_id: scannerRow.market_id ?? res.market_id,
        cid: cid ?? res.cid,
        platform: scannerRow.platform ?? res.platform,
        market_url: scannerRow.market_url ?? res.market_url,
        market_type: scannerRow.market_type ?? res.market_type,
        market_question: scannerRow.question ?? res.market_question,
        availability: (scannerRow.market_id && scannerRow.market_url)
          ? { status: "available" }
          : res.availability,
        yes_bid: scannerRow.yes_bid ?? (res as any).yes_bid ?? null,
        yes_ask: scannerRow.yes_ask ?? (res as any).yes_ask ?? null,
        spread: scannerRow.spread ?? (res as any).spread ?? null,
        end_date: scannerRow.end_date ?? (res as any).end_date ?? null,
        total_volume: scannerRow.total_volume ?? (res as any).total_volume ?? null,
        total_liquidity: scannerRow.total_liquidity ?? (res as any).total_liquidity ?? null,
        decision: scannerRow.decision ?? res.decision,
        confidence: scannerRow.confidence ?? res.confidence,
        edge_pct: scannerRow.edge_pct ?? res.edge_pct,
        edge_label: (scannerRow.edge_label ?? res.edge_label) as any,
        market_probability: scannerRow.market_probability ?? res.market_probability,
        estimated_probability: scannerRow.estimated_probability ?? res.estimated_probability,
        forced_winner: scannerRow.forced_winner ?? res.forced_winner,
        options: (Array.isArray(scannerRow.options) && scannerRow.options.length > 0
          ? scannerRow.options : res.options) as any,
        best_option: scannerRow.best_option ?? res.best_option,
        best_option_edge: scannerRow.best_option_edge ?? res.best_option_edge,
        source_status: scannerRow.market_probability != null ? "live" : (res.source_status ?? "analysis_only"),
        source_notes: scannerRow.market_probability != null
          ? [
              `Verified from the live ${scannerRow.platform} scanner row captured ${scannerRow.scanned_at ?? "recently"}.`,
              ...(res.source_notes ?? []),
            ]
          : res.source_notes,
        scanner_truth: true,
        scanned_at: scannerRow.scanned_at ?? null,
      } : res;
      // FIRST-QUERY GUARDRAIL — if a brand-new visitor's first query lands on a
      // broken/stale market, override with a known live market_feed row so the
      // first impression is always the product working perfectly. Tracked once
      // per browser via localStorage; subsequent queries see honest results.
      let finalResult = merged;
      try {
        const FLAG = "decyzon_first_query_done";
        const isFirst = typeof window !== "undefined" && !window.localStorage.getItem(FLAG);
        if (isFirst) {
          const derived = deriveResultStateFull({
            result: merged,
            livePrice: null,
            liveFetchedAt: null,
            liveSource: "none",
          });
          const broken = derived.state === "broken_route" || derived.state === "stale_snapshot";
          if (broken) {
            const { data: liveRow } = await supabase
              .from("market_feed")
              .select("market_id, market_url")
              .eq("is_actionable", true)
              .eq("route_status", "ok")
              .eq("platform", "polymarket")
              .order("ranking_score", { ascending: false, nullsFirst: false })
              .limit(1)
              .maybeSingle();
            if (liveRow?.market_url) {
              // Pass cid so this hits the same prewarmed cache row.
              const liveRes = await callPredict(liveRow.market_url, { cid: liveRow.market_id });
              finalResult = liveRes;
              toast({
                title: "Showing a live setup",
                description: "Your search returned a stale market — here's a verified live one.",
              });
            }
          }
          try { window.localStorage.setItem(FLAG, "1"); } catch {}
        }
      } catch { /* guardrail is best-effort */ }
      setResult(finalResult);
      if (user) {
        try {
          const { data: inserted } = await supabase.from("market_decisions").insert({
            user_id: user.id,
            raw_input: q,
            decision: finalResult.decision,
            edge_label: finalResult.edge_label,
            edge_pct: finalResult.edge_pct,
            market_id: finalResult.market_id,
            platform: finalResult.platform,
            market_question: finalResult.market_question,
            market_url: finalResult.market_url,
            market_probability: finalResult.market_probability,
            estimated_probability: finalResult.estimated_probability,
            forced_winner: finalResult.forced_winner,
            side: finalResult.side,
            action: finalResult.action,
            entry_zone: finalResult.entry_zone,
            invalidation: finalResult.invalidation,
            recheck_when: finalResult.recheck_when,
            why: finalResult.why,
            trap: finalResult.trap,
            confidence: finalResult.confidence,
            signals_used: finalResult.signals_used as any,
            market_missing: finalResult.market_missing,
            contenders: finalResult.contenders as any,
            related_markets: finalResult.related_markets as any,
            scenarios: finalResult.scenarios as any,
            result_payload: { ...finalResult, _saved_plan: plan } as any,
          }).select("id").maybeSingle();
          if (inserted?.id) setDecisionId(inserted.id);
        } catch (e) { console.warn("Decision log failed", e); }
      }
      // Refresh plan/trial counters so UsageCounterLine visibly decrements.
      try { await refreshPlan(); } catch {}
    } catch (e: any) {
      if (e instanceof QuotaExceededError) {
        // Guests hit the daily anonymous cap → route to sign-in (free), not paid upgrade.
        // Signed-in users on a paid-eligible tier → upgrade modal.
        if (e.tier === "guest" || !user) {
          setError("Daily free limit reached. Sign in to continue — it's free.");
          toast({
            title: "Sign in to keep deciding",
            description: "Free accounts get more daily decisions and save your history.",
          });
        } else {
          setError(`${e.message} Resets ${new Date(e.resets_at).toLocaleString()}.`);
          setUpgradeFeature("More decisions");
          setUpgradeOpen(true);
        }
      } else {
        setError(e?.message ?? "Prediction failed");
      }
    } finally {
      setLoading(false);
    }
  };

  // Autorun: react to URL changes too. Forwards cid for contract identity lock.
  useEffect(() => {
    const autorun = params.get("autorun");
    const q = params.get("q") ?? "";
    const cid = params.get("cid");
    if (autorun === "1" && q.trim() && !loading) {
      if (q !== input) setInput(q);
      // Phase 1: short-circuit Kalshi URLs arriving via deep link / external referral.
      // Show the unsupported state in the input area instead of calling predict().
      const incoming = parseMarketInput(q);
      if (incoming.reason === "kalshi_unavailable") {
        setResult(null);
        setDecisionId(null);
        setError(null);
        params.delete("autorun");
        params.delete("cid");
        setParams(params, { replace: true });
        return;
      }
      setResult(null);
      setDecisionId(null);
      setError(null);
      void run(q, cid);
      params.delete("autorun");
      params.delete("cid");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("autorun"), params.get("q"), params.get("cid")]);

  // ?welcome=1 — fresh signup landing. Auto-run the top actionable Polymarket
  // market so a brand new account never lands on a static screen. Runs once,
  // strips the param, and only fires for signed-in users with no result yet.
  useEffect(() => {
    if (params.get("welcome") !== "1") return;
    if (!user || loading || result) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await supabase
          .from("market_feed")
          .select("market_url, market_id")
          .eq("platform", "polymarket")
          .eq("is_actionable", true)
          .eq("route_status", "ok")
          .not("market_url", "is", null)
          .order("ranking_score", { ascending: false, nullsFirst: false })
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        if (data?.market_url) {
          await run(data.market_url, data.market_id ?? null);
        }
      } catch { /* swallow */ }
      params.delete("welcome");
      setParams(params, { replace: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("welcome"), user]);

  const onSave = async () => {
    if (!user) {
      toast({ title: "Sign in to save markets", description: "Create a free account to track your watchlist." });
      return;
    }
    if (!result) return;
    try {
      // Tier limit: Free/Guest capped at 1 active recheck trigger.
      const { canAddActiveWatch } = await import("@/lib/watchlistLimits");
      const gate = await canAddActiveWatch(user.id, plan);
      if (!gate.allowed) {
        toast({
          title: "Free plan limit reached",
          description: `You can track ${gate.limit} market at a time. Remove one or upgrade to Edge for unlimited rechecks.`,
          variant: "destructive",
        });
        return;
      }
      await supabase.from("market_watchlist").insert({
        user_id: user.id,
        market_id: result.market_id,
        platform: result.platform,
        market_question: result.market_question,
        market_url: result.market_url,
        thesis: result.why,
        last_market_probability: result.market_probability,
        recheck_conditions: result.recheck_when,
        // Phase 2 — snapshot the engine state at save time so the watchlist
        // can show an honest "what changed since you saved this" diff.
        saved_decision: result.decision ?? null,
        saved_edge_pct: result.edge_pct ?? null,
        saved_market_probability: result.market_probability ?? null,
        saved_confidence: result.confidence ?? null,
        saved_trap: result.trap ?? null,
        saved_at_scan: new Date().toISOString(),
      });
      toast({ title: "Added to watchlist" });
    } catch (e: any) {
      toast({ title: "Save failed", description: e?.message, variant: "destructive" });
    }
  };

  const onUpgrade = (feature: string) => {
    setUpgradeFeature(feature);
    setUpgradeOpen(true);
  };

  return (
    <PageLayout wide>
      <JsonLd
        id="home-faq"
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: HOME_FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />

      {/* SIGNED-IN COCKPIT — daily command center for authenticated users.
          Routes them past the marketing hero into a SinceLastVisit-led layout. */}
      {!result && !loading && user && (
        <Suspense fallback={null}>
          <SignedInHome
            onUpgrade={(f) => { setUpgradeFeature(f); setUpgradeOpen(true); }}
          />
        </Suspense>
      )}

      {/* SIGNED-OUT MARKETING HERO — first-impression surface for cold traffic. */}
      {!result && !loading && !user && (
        <>
          <HeroLiveDecisions onRun={(q, cid) => { setInput(q); void run(q, cid); }} loading={loading} />

          {/* NO-CLEAN-BUY FALLBACK — turns the empty state into a value
              statement when no Strong/Playable BUY is live. Self-hides
              automatically when at least one clean BUY exists. */}
          <NoCleanBuyFallback />

          {/* Secondary: paste-a-URL fallback for users who already have a market in mind. */}
          <div className="max-w-2xl mx-auto">
            <PasteLinkBar variant="secondary" />
          </div>

          {/* PROOF STRIP — single consolidated row (hit rate / live edges / last scan). */}
          <HomeProofStrip />

          {/* EDGE DROP — premium proof element, kept above the fold. */}
          <Suspense fallback={null}>
            <EdgeDropHero onUpgrade={(f) => { setUpgradeFeature(f); setUpgradeOpen(true); }} />
          </Suspense>
        </>
      )}

      {error && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 mb-4">
          <p className="text-xs font-body text-destructive">{error}</p>
        </div>
      )}

      {loading && (
        <LoadingTradeSkeleton input={input} />
      )}

      {result && (
        <Suspense fallback={
          <div className="rounded-xl border border-border/60 bg-card p-8 text-center">
            <Loader2 className="w-5 h-5 animate-spin text-primary mx-auto mb-2" />
            <p className="text-xs font-body text-muted-foreground">Loading result…</p>
          </div>
        }>
          <PredictionResultPage
            result={result}
            plan={plan}
            decisionId={decisionId}
            onSave={onSave}
            onRedecide={() => run(input)}
            onUpgrade={onUpgrade}
          />
        </Suspense>
      )}

      <Suspense fallback={null}>
        {!result && !loading && <PricingSection />}
        {!result && !loading && <WhatIsDecyzon />}

        <UpgradeModal
          open={upgradeOpen}
          onClose={() => setUpgradeOpen(false)}
          requiredFeature={upgradeFeature}
        />
        <SiteFooter />
      </Suspense>
    </PageLayout>
  );
}
